import {
  Button,
  ButtonProps,
  buttonBaseClasses,
  IconButton,
  IconButtonProps,
  styled,
  Theme,
} from '@chronosphereio/chrono-ui';
import { forwardRef } from 'react';

export interface CombinedControlsIconButtonProps extends Omit<IconButtonProps, 'variant' | 'size'> {}

/**
 * Internal-only wrapper for icon buttons that should be used in concert with `CombinedControls` for
 * a consistent look and feel.
 */
export const CombinedControlsIconButton = forwardRef<HTMLButtonElement, CombinedControlsIconButtonProps>(
  function CombinedControlsIconButton(props, ref) {
    return <CombinedControlsIconButtonWrapper {...props} ref={ref} variant="secondary" size="medium" />;
  }
);

export interface CombinedControlsButtonProps extends Omit<ButtonProps, 'variant' | 'size'> {}

/**
 * Internal-only wrapper for buttons that should be used in concert with `CombinedControls` for
 * a consistent look and feel.
 */
export const CombinedControlsButton = forwardRef<HTMLButtonElement, CombinedControlsButtonProps>(
  function CombinedControlsIconButton(props, ref) {
    return <CombinedControlsButtonWrapper {...props} ref={ref} variant="secondary" size="small" />;
  }
);

const getCommonCombinedButtonStyles = (theme: Theme) => {
  if (theme.featureFlags.panwRebrandStubYear) {
    return {
      background: theme.palette.background.container,
      border: `1px solid ${theme.palette.border.default}`,
      '&:hover': {
        background: theme.palette.background.container,
        border: `1px solid ${theme.palette.border.bold}`,
      },
      '&:disabled': {
        background: 'transparent',
        border: `1px solid ${theme.palette.border.disabled}`,
      },
      [`&.${buttonBaseClasses.focusVisible}`]: {
        // Adds a border highlight (similar to what the select does) for consistency.
        border: `1px solid ${theme.palette.border.highlight}`,
        // Simulates an increase of border size to 2px on focus without needing to
        // fight with the height/width of the button changing when going from 1px to 2px border.
        boxShadow: `inset 0px 0px 0px 1px ${theme.palette.border.highlight}`,
      },
    };
  }

  return {
    background: theme.palette.background.container,
    [`&.${buttonBaseClasses.focusVisible}`]: {
      // [legacy] Adds a border highlight (similar to what the select does) for consistency.
      borderColor: theme.palette.primary.main,
      // [legacy] Simulates an increase of border size to 2px on focus without needing to
      // fight with the height/width of the button changing when going from 1px to 2px border.
      boxShadow: `inset 0px 0px 0px 1px ${theme.palette.primary.main}`,
    },
  };
};

export const CombinedControlsIconButtonWrapper = styled(IconButton)(({ theme }) => {
  return getCommonCombinedButtonStyles(theme);
});

export const CombinedControlsButtonWrapper = styled(Button)(({ theme }) => {
  return { ...getCommonCombinedButtonStyles(theme), fontWeight: theme.typography.fontWeightRegular };
});
